import { useState, useEffect } from 'react';
import axios from 'axios';
import { History, Search, Filter, User, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [filterEntity]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filterEntity) params.append('entity_type', filterEntity);
      params.append('limit', '100');
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-emerald-100 text-emerald-700';
      case 'UPDATE': return 'bg-amber-100 text-amber-700';
      case 'DELETE': return 'bg-rose-100 text-rose-700';
      case 'IMPORT': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const entityTypes = [...new Set(logs.map(l => l.entity_type))];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
        <p className="text-slate-500">Track all system changes and activities</p>
      </div>

      <div className="flex gap-4">
        <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Entity Types</option>
          {entityTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-900">{log.user_email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600 capitalize">{log.entity_type}</span>
                    <span className="text-xs text-slate-400 block font-mono">{log.entity_id.slice(0, 8)}...</span>
                  </td>
                  <td className="px-4 py-3">
                    <pre className="text-xs text-slate-600 bg-slate-50 p-2 rounded max-w-xs overflow-hidden">
                      {JSON.stringify(log.details, null, 2).slice(0, 100)}...
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {logs.length === 0 && (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No audit logs found</p>
        </div>
      )}
    </div>
  );
}
